import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { HttpXhrBackend } from '@angular/common/http';
import { ActionManagementService } from './services/action-management.service';
import { AuthenticateService } from './services/authenticate/authenticate.service';
import { BulkUploadService } from './services/bulk-upload.service';
import { CollaborationReviewService } from './services/collaboration-review.service';
import { ConfigService } from './services/config.service';
import { CountryService } from './services/country.service';
import { DashboardService } from './services/dashboard.service';
import { DocumentManagerService } from './services/document-manager.service';
import { DocumentTypeService } from './services/document-type.service';
import { EMailService } from './services/e-mail.service';
import { QAService } from './services/qa.service';
import { FileUploadService } from './services/file-upload.service';
import { JobManagementService } from './services/job-management.service';
import { EventManagementService } from './services/event-management.service';
import { SiteService } from './services/site.service';
import { CommunicationLogService } from './services/communication-log.service';
import { SubmissionProfileService } from './services/submission-profile.service';
import { CalendarTaskService } from './services/calendar-tasks.service';
import { TopicSearchService } from './services/topic-search.service';
import { TopicService } from './services/topic.service';
import { UserGroupService } from './services/user-group.service';
import { UserService } from './services/user.service';
import { ProgramService } from './services/program.service';
import { ExportService } from './services/export.service';
import { SubscribeService } from './services/subscribe.service';
import { WorkflowManagementService } from '@app/origami-api/services/workflow-management.service';
import { QueryTicketService } from '@app/origami-api/services/query-ticket.service';
import { LMSService } from '@app/origami-api/services/lms.service';
import { SponsorService } from './services/sponsor.service';
import { EssentialDocumentsService } from '@app/origami-api/services/essential-documents.service';
import { RequiredDocumentService } from '@app/origami-api/services/required-document.service';
import { IntegrationService } from '@app/origami-api/services/integration.service';
import { ESignatureService } from '@app/origami-api/services/e-signature.service';
import { InstitutionalReviewBoardService } from '@app/origami-api/services/institutional-review-board.service';
import { TemplateService } from './services/template.service';
import { RespondQueryService } from '@app/ti-components/documents/documents-actions/components/documents-coding/documents-queries/services/respond-query.service';
import { CASAuthRequestProxy } from '@app/origami-api/services/base/request-proxy/cas-auth.request-proxy.class';
import { DocumentTemplateService } from '@app/origami-api/services/document-template.service';
import { InvestigativeSiteReportService } from './services/investigative-site-report.service';
import { StudyStartupService } from './services/study-startup.service';
import { SharedWorkspaceService } from '@app/origami-api/services/shared-workspace.service';
import { TeamsService } from '@app/origami-api/services/teams.service';
import { ErrorDescriptionService } from './services/error-description.service';
import { DefaultFieldTitlesService } from './services/default-field-titles.service';
import { StudyProfileService } from '@app/origami-api/services/study-profile.service';
import { TranslationsService } from '@app/origami-api/services/translations.service';
import { IndexSearchService } from '@app/origami-api/services/index-search.service';
import { AmendmentProfileService } from './services/amendment-profile.service';
import { CountriesBulkUploadService } from '@app/origami-api/services/countries-bulk-upload.service';
import { AcknowledgeService } from './services/acknowledge.service';
import { OrganizationService } from '@app/origami-api/services/organization.service';
import { TopicTypesService } from './services/topic-types.service';
import { TimeZoneService } from '@app/services/time-zone.service';
import { NavigationApiService } from './services/navigation-api.service';

@NgModule()
export class OrigamiApiModule {
  static forRoot(): ModuleWithProviders<OrigamiApiModule> {
    return {
      ngModule: OrigamiApiModule,
      providers: [
        CASAuthRequestProxy,
        {
          provide: ActionManagementService,
          useClass: ActionManagementService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: AuthenticateService,
          useClass: AuthenticateService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: BulkUploadService,
          useClass: BulkUploadService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: ConfigService,
          useClass: ConfigService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: CountryService,
          useClass: CountryService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: DashboardService,
          useClass: DashboardService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: DocumentManagerService,
          useClass: DocumentManagerService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: DocumentTypeService,
          useClass: DocumentTypeService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: EMailService,
          useClass: EMailService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: QAService,
          useClass: QAService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: FileUploadService,
          useClass: FileUploadService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: JobManagementService,
          useClass: JobManagementService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: EventManagementService,
          useClass: EventManagementService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: InstitutionalReviewBoardService,
          useClass: InstitutionalReviewBoardService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: SiteService,
          useClass: SiteService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: CommunicationLogService,
          useClass: CommunicationLogService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: AmendmentProfileService,
          useClass: AmendmentProfileService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: SubmissionProfileService,
          useClass: SubmissionProfileService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: CalendarTaskService,
          useClass: CalendarTaskService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: TopicSearchService,
          useClass: TopicSearchService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: TopicService,
          useClass: TopicService,
          deps: [HttpXhrBackend, CASAuthRequestProxy, TimeZoneService],
        },
        {
          provide: TopicTypesService,
          useClass: TopicTypesService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: UserGroupService,
          useClass: UserGroupService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: UserService,
          useClass: UserService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: CollaborationReviewService,
          useClass: CollaborationReviewService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: ProgramService,
          useClass: ProgramService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: StudyProfileService,
          useClass: StudyProfileService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: TranslationsService,
          useClass: TranslationsService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: ExportService,
          useClass: ExportService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: TemplateService,
          useClass: TemplateService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: SubscribeService,
          useClass: SubscribeService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: WorkflowManagementService,
          useClass: WorkflowManagementService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: QueryTicketService,
          useClass: QueryTicketService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: LMSService,
          useClass: LMSService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: SponsorService,
          useClass: SponsorService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: EssentialDocumentsService,
          useClass: EssentialDocumentsService,
          deps: [HttpXhrBackend, CASAuthRequestProxy, TimeZoneService],
        },
        {
          provide: IntegrationService,
          useClass: IntegrationService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: ESignatureService,
          useClass: ESignatureService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: RequiredDocumentService,
          useClass: RequiredDocumentService,
          deps: [HttpXhrBackend, CASAuthRequestProxy, TimeZoneService],
        },
        {
          provide: ESignatureService,
          useClass: ESignatureService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: RespondQueryService,
          useClass: RespondQueryService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: DocumentTemplateService,
          useClass: DocumentTemplateService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: InvestigativeSiteReportService,
          useClass: InvestigativeSiteReportService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: StudyStartupService,
          useClass: StudyStartupService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: SharedWorkspaceService,
          useClass: SharedWorkspaceService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: TeamsService,
          useClass: TeamsService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: ErrorDescriptionService,
          useClass: ErrorDescriptionService,
        },
        {
          provide: DefaultFieldTitlesService,
          useClass: DefaultFieldTitlesService,
        },
        {
          provide: IndexSearchService,
          useClass: IndexSearchService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: CountriesBulkUploadService,
          useClass: CountriesBulkUploadService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: AcknowledgeService,
          useClass: AcknowledgeService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: OrganizationService,
          useClass: OrganizationService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
        {
          provide: NavigationApiService,
          useClass: NavigationApiService,
          deps: [HttpXhrBackend, CASAuthRequestProxy],
        },
      ],
    };
  }

  constructor(@Optional() @SkipSelf() parentModule: OrigamiApiModule) {
    if (parentModule) {
      throw new Error('OrigamiApiModule is already loaded. Import it in the AppModule only');
    }
  }
}
